import React from "react";
import { Box, Radio, Stack, Typography } from "@mui/material";

export interface MarkerTypeOptionProps {
  /** Whether this option is the selected one. */
  selected: boolean;
  /** Title line, e.g. "Phase Change". */
  title: string;
  /** Supporting description, e.g. "Protocol or phase transition". */
  description: string;
  /** Marker sample style — solid line for phase change, dashed for events. */
  sample: "solid" | "dashed";
  /** Called when the card (or its radio) is selected. */
  onSelect: () => void;
  /** Makes the option read-only, e.g. when editing a marker whose type can't change. */
  disabled?: boolean;
}

/**
 * Selectable card for a marker type. Renders a radio, a title + description,
 * and a sample of how the marker line appears on the graph (solid vs dashed).
 * The whole card is clickable and exposes `radio` semantics for accessibility.
 *
 * @param props
 * @param props.selected    - Whether this option is currently chosen
 * @param props.title       - Option title
 * @param props.description - Supporting copy below the title
 * @param props.sample      - "solid" (phase change) or "dashed" (external event)
 * @param props.onSelect    - Selection handler
 * @param [props.disabled]  - Read-only: not selectable, unselected option dimmed
 */
const MarkerTypeOption: React.FC<MarkerTypeOptionProps> = ({
  selected,
  title,
  description,
  sample,
  onSelect,
  disabled = false,
}) => {
  // Keep the chosen option readable; only the one that can't be picked dims.
  const dimmed = disabled && !selected;

  return (
    // The card is a clickable container; the MUI Radio carries the radio
    // semantics (role + accessible name) so there's a single focus/queryable
    // control per option.
    <Box
      onClick={disabled ? undefined : onSelect}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 5,
        py: 5,
        cursor: disabled ? "default" : "pointer",
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "primary.background" : "background.paper",
        borderRadius: "10px",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": disabled ? undefined : { borderColor: "primary.main" },
      }}
    >
      <Box component="section" sx={{ height: "stretch", mt: "-2px" }}>
        <Radio
          checked={selected}
          disabled={dimmed}
          size="small"
          color="primary"
          inputProps={{ "aria-label": title, "aria-disabled": disabled || undefined }}
          sx={{ p: 0 }}
        />
      </Box>

      <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight="400" color={dimmed ? "text.disabled" : "text.primary"}>
          {title}
        </Typography>
        <Typography variant="body.caption" color={dimmed ? "text.disabled" : "text.secondary"}>
          {description}
        </Typography>
      </Stack>

      {/* Marker sample — mirrors how the annotation is drawn on the graph. */}
      <Box
        aria-hidden
        sx={{
          width: 40,
          borderBottomWidth: "2px",
          borderBottomStyle: sample === "dashed" ? "dashed" : "solid",
          borderBottomColor: dimmed ? "text.disabled" : "primary.main",
        }}
      />
    </Box>
  );
};

export default MarkerTypeOption;
